import { cn } from "@d12labs/utils";
import { Search } from "lucide-react";
import type * as React from "react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";

/**
 * Command palette — the power-user accelerator (Linear-cmdk). Self-contained:
 * a portaled overlay on `overlay-surface` with the elevation shadow, fuzzy
 * substring filter, full keyboard nav (↑/↓/Enter/Esc), and mouse-hover sync.
 * No Radix Dialog dependency — focus + scroll-lock are handled inline.
 *
 * It's presentational + interaction only: the app feeds it `items` (built from
 * the plugin registry, core routes, actions) and owns the open state. Mount the
 * global ⌘K listener where you render it (see AppShell).
 */
export type CommandItem = {
	id: string;
	label: string;
	/** Group heading the item sorts under (preserves insertion order). */
	group?: string;
	/** Extra match terms beyond the label. */
	keywords?: string[];
	icon?: React.ComponentType<{ className?: string }>;
	/** Right-aligned hint, e.g. "⌘K" or a route. */
	hint?: string;
	onSelect: () => void;
};

function matches(item: CommandItem, q: string): boolean {
	if (!q) return true;
	const hay = `${item.label} ${item.group ?? ""} ${(item.keywords ?? []).join(" ")}`.toLowerCase();
	// All whitespace-separated tokens must appear (order-independent).
	return q
		.toLowerCase()
		.split(/\s+/)
		.filter(Boolean)
		.every((tok) => hay.includes(tok));
}

export function CommandPalette({
	open,
	onOpenChange,
	items,
	placeholder = "Type a command or search…",
	emptyText = "No results",
}: {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	items: CommandItem[];
	placeholder?: string;
	emptyText?: string;
}) {
	const [query, setQuery] = useState("");
	const [active, setActive] = useState(0);
	const inputRef = useRef<HTMLInputElement>(null);
	const listRef = useRef<HTMLDivElement>(null);

	const filtered = useMemo(() => items.filter((it) => matches(it, query)), [items, query]);

	// Ordered groups → flat list with group headers interleaved, plus the index
	// map the keyboard nav walks (only selectable rows count).
	const groups = useMemo(() => {
		const order: string[] = [];
		const byGroup = new Map<string, CommandItem[]>();
		for (const it of filtered) {
			const g = it.group ?? "";
			if (!byGroup.has(g)) {
				byGroup.set(g, []);
				order.push(g);
			}
			byGroup.get(g)?.push(it);
		}
		return order.map((g) => ({ group: g, items: byGroup.get(g) ?? [] }));
	}, [filtered]);

	// Reset on each open; focus the input.
	useEffect(() => {
		if (!open) return;
		setQuery("");
		setActive(0);
		const id = requestAnimationFrame(() => inputRef.current?.focus());
		return () => cancelAnimationFrame(id);
	}, [open]);

	// Clamp the active row when the result set shrinks.
	useEffect(() => {
		setActive((a) => Math.min(a, Math.max(0, filtered.length - 1)));
	}, [filtered.length]);

	// Lock background scroll while open.
	useEffect(() => {
		if (!open) return;
		const prev = document.body.style.overflow;
		document.body.style.overflow = "hidden";
		return () => {
			document.body.style.overflow = prev;
		};
	}, [open]);

	// Keep the active row visible.
	useEffect(() => {
		listRef.current
			?.querySelector(`[data-cmd-index="${active}"]`)
			?.scrollIntoView({ block: "nearest" });
	}, [active]);

	const choose = useCallback(
		(item: CommandItem | undefined) => {
			if (!item) return;
			onOpenChange(false);
			item.onSelect();
		},
		[onOpenChange],
	);

	const onKeyDown = (e: React.KeyboardEvent) => {
		if (e.key === "ArrowDown") {
			e.preventDefault();
			setActive((a) => (filtered.length ? (a + 1) % filtered.length : 0));
		} else if (e.key === "ArrowUp") {
			e.preventDefault();
			setActive((a) => (filtered.length ? (a - 1 + filtered.length) % filtered.length : 0));
		} else if (e.key === "Enter") {
			e.preventDefault();
			choose(filtered[active]);
		} else if (e.key === "Escape") {
			e.preventDefault();
			onOpenChange(false);
		}
	};

	if (!open || typeof document === "undefined") return null;

	let runningIndex = -1;

	return createPortal(
		<div className="fixed inset-0 z-[100] flex items-start justify-center p-4 pt-[14vh]">
			{/* Backdrop is a real button so dismiss is keyboard-reachable; the panel
			    is a sibling (not a child), so panel clicks never bubble to it. */}
			<button
				type="button"
				aria-label="Close command palette"
				className="absolute inset-0 bg-scrim/60"
				onClick={() => onOpenChange(false)}
			/>
			<div
				role="dialog"
				aria-modal="true"
				aria-label="Command palette"
				className="relative w-full max-w-lg overflow-hidden rounded-xl border border-hairline-strong bg-overlay-surface shadow-[var(--shadow-overlay)]"
				onKeyDown={onKeyDown}
			>
				<div className="flex items-center gap-2.5 border-b border-hairline px-3.5">
					<Search className="size-4 flex-none text-ink-subtle" />
					<input
						ref={inputRef}
						value={query}
						onChange={(e) => {
							setQuery(e.target.value);
							setActive(0);
						}}
						placeholder={placeholder}
						className="h-11 w-full bg-transparent text-body text-foreground outline-none placeholder:text-ink-subtle"
						aria-label={placeholder}
					/>
				</div>

				<div ref={listRef} className="max-h-[min(60vh,380px)] overflow-y-auto p-1.5">
					{filtered.length === 0 ? (
						<div className="px-3 py-8 text-center text-body-sm text-ink-subtle">{emptyText}</div>
					) : (
						groups.map(({ group, items: groupItems }) => (
							<div key={group || "_"} className="mb-1 last:mb-0">
								{group ? (
									<div className="eyebrow px-2 pb-1 pt-2 text-ink-subtle">{group}</div>
								) : null}
								{groupItems.map((it) => {
									runningIndex += 1;
									const idx = runningIndex;
									const isActive = idx === active;
									const Icon = it.icon;
									return (
										<button
											key={it.id}
											type="button"
											data-cmd-index={idx}
											onMouseMove={() => setActive(idx)}
											onClick={() => choose(it)}
											className={cn(
												"flex w-full items-center gap-2.5 rounded-md px-2 py-2 text-left text-body-sm transition-colors",
												isActive ? "bg-surface-3 text-foreground" : "text-ink-muted",
											)}
										>
											{Icon ? (
												<Icon
													className={cn(
														"size-4 flex-none",
														isActive ? "text-foreground" : "text-ink-subtle",
													)}
												/>
											) : null}
											<span className="min-w-0 flex-1 truncate">{it.label}</span>
											{it.hint ? (
												<span className="flex-none text-meta text-ink-tertiary">{it.hint}</span>
											) : null}
										</button>
									);
								})}
							</div>
						))
					)}
				</div>
			</div>
		</div>,
		document.body,
	);
}
